import notification from "antd/es/notification";
import { Task } from "redux-saga";
import { call, cancel, delay, fork, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import LastSyncStatus from "@mapmycustomers/shared/enum/resources/LastSyncStatus";
import SyncProgressStatus from "@mapmycustomers/shared/enum/resources/SyncProgressStatus";
import Organization from "@mapmycustomers/shared/types/Organization";
import StorageConnection from "@mapmycustomers/shared/types/resources/StorageConnection";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import i18nService from "@app/config/I18nService";
import { getProviderDisplayName } from "@app/scene/resources/components/StorageConnections/util/getProviderDisplayName";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import BrowseResponse from "@app/types/resources/BrowseResponse";

import resourcesSettings from "../../utils/resourcesSettings";
import { fetchFolders, fetchResources } from "../actions";

import {
  addPollingConnectionId,
  browseExternalFolders,
  createConnection,
  deleteConnection,
  fetchConnections,
  removePollingConnectionId,
  syncExternalFolders,
  triggerManualSync,
  updateConnection,
  updateConnectionInStore,
} from "./actions";
import messages from "./messages";

const POLL_INTERVAL_MS = 3000;
const MAX_CONSECUTIVE_ERRORS = 3;

const pollingTasks = new Map<StorageConnection["id"], Task>();

export function* onFetchConnections() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<StorageConnection> = yield callApi(
      "fetchStorageConnections",
      orgId,
      { $order: "-updatedAt" }
    );
    yield put(fetchConnections.success(response.data));
  } catch (error) {
    yield put(fetchConnections.failure());
    yield put(handleError({ error }));
  }
}

export function* onCreateConnection({
  payload: { callback, data },
}: ReturnType<typeof createConnection.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const response: StorageConnection = yield callApi("createStorageConnection", orgId, data);
    yield put(createConnection.success(response));

    if (response.authUrl) {
      resourcesSettings.setOAuthConnectionData(response.id, response.provider);
      window.location.href = response.authUrl;
      return;
    }

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({ message: intl.formatMessage(messages.createSuccess) });
    }
    if (callback) {
      yield call(callback, response);
    }
  } catch (error) {
    yield put(createConnection.failure());
    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({ message: intl.formatMessage(messages.createError) });
    }
    yield put(handleError({ error }));
  }
}

export function* onUpdateConnection({
  payload: { id, callback, data, failureCallback },
}: ReturnType<typeof updateConnection.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const connection: StorageConnection = yield callApi("updateStorageConnection", orgId, id, data);
    yield put(updateConnection.success(connection));
    if (callback) {
      yield call(callback, connection);
    }
  } catch (error) {
    yield put(updateConnection.failure());
    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({ message: intl.formatMessage(messages.updateError) });
    }
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

export function* onDeleteConnection({
  payload: { id, callback },
}: ReturnType<typeof deleteConnection.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteStorageConnection", orgId, id);
    yield put(deleteConnection.success(id));
    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({ message: intl.formatMessage(messages.deleteSuccess) });
    }
    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    yield put(deleteConnection.failure());
    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({ message: intl.formatMessage(messages.deleteError) });
    }
    yield put(handleError({ error }));
  }
}

export function* onBrowseExternalFolders({
  payload: { callback, connectionId, failureCallback, folderId, pageToken },
}: ReturnType<typeof browseExternalFolders.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: BrowseResponse = yield callApi("browseExternalFolders", orgId, connectionId, {
      folderId,
      pageToken,
    });
    yield put(browseExternalFolders.success(response));
    if (callback) {
      yield call(callback, response);
    }
  } catch (error) {
    yield put(browseExternalFolders.failure());
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* startPolling(connectionId: StorageConnection["id"]) {
  const existingTask = pollingTasks.get(connectionId);
  if (existingTask) {
    yield cancel(existingTask);
  }

  yield put(addPollingConnectionId(connectionId));
  const task: Task = yield fork(pollSyncProgress, connectionId);
  pollingTasks.set(connectionId, task);
}

export function* pollSyncProgress(connectionId: StorageConnection["id"]) {
  let consecutiveErrors = 0;
  let lastConnection: StorageConnection | undefined;

  try {
    while (true) {
      yield delay(POLL_INTERVAL_MS);

      try {
        const orgId: Organization["id"] = yield select(getOrganizationId);

        const connection: StorageConnection = yield callApi(
          "fetchStorageConnection",
          orgId,
          connectionId
        );
        yield put(updateConnectionInStore(connection));
        consecutiveErrors = 0;
        lastConnection = connection;

        const isInProgress = connection.syncProgress?.status === SyncProgressStatus.IN_PROGRESS;
        if (!isInProgress) {
          break;
        }
      } catch (error) {
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          yield put(handleError({ error }));
          const intl = i18nService.getIntl();
          if (intl) {
            notification.error({ message: intl.formatMessage(messages.syncPollError) });
          }
          break;
        }
      }
    }

    if (consecutiveErrors < MAX_CONSECUTIVE_ERRORS && lastConnection?.lastSync) {
      const intl = i18nService.getIntl();
      if (intl) {
        const { status } = lastConnection.lastSync;
        const provider = getProviderDisplayName(intl, lastConnection.provider);
        if (status === LastSyncStatus.COMPLETED) {
          notification.success({
            message: intl.formatMessage(messages.syncCompleted, { provider }),
          });
        } else if (status === LastSyncStatus.COMPLETED_WITH_ERRORS) {
          notification.warning({
            message: intl.formatMessage(messages.syncCompletedWithErrors, { provider }),
          });
        } else if (status === LastSyncStatus.FAILED) {
          notification.error({
            message: intl.formatMessage(messages.syncFailed, { provider }),
          });
        }
      }
    }
  } finally {
    pollingTasks.delete(connectionId);
    yield put(removePollingConnectionId(connectionId));

    yield put(fetchFolders.request());
    yield put(fetchResources.request({}));
  }
}

export function* onSyncExternalFolders({
  payload: { callback, connectionId, items },
}: ReturnType<typeof syncExternalFolders.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("syncExternalFolders", orgId, connectionId, { items });
    yield put(syncExternalFolders.success());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({ message: intl.formatMessage(messages.syncStarted) });
    }

    yield* startPolling(connectionId);

    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    yield put(syncExternalFolders.failure());
    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({ message: intl.formatMessage(messages.syncError) });
    }
    yield put(handleError({ error }));
  }
}

export function* onTriggerManualSync({
  payload: { callback, connectionId },
}: ReturnType<typeof triggerManualSync.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    yield callApi("triggerManualSync", orgId, connectionId);
    yield put(triggerManualSync.success());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({ message: intl.formatMessage(messages.syncStarted) });
    }

    yield* startPolling(connectionId);

    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    yield put(triggerManualSync.failure());
    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({ message: intl.formatMessage(messages.syncError) });
    }
    yield put(handleError({ error }));
  }
}

export function* externalResourcesSaga() {
  yield takeLatest(fetchConnections.request, onFetchConnections);
  yield takeLatest(createConnection.request, onCreateConnection);
  yield takeLatest(updateConnection.request, onUpdateConnection);
  yield takeLatest(deleteConnection.request, onDeleteConnection);
  yield takeEvery(browseExternalFolders.request, onBrowseExternalFolders);
  yield takeEvery(syncExternalFolders.request, onSyncExternalFolders);
  yield takeEvery(triggerManualSync.request, onTriggerManualSync);
}
